import React, { useRef } from 'react';
import { Download, Printer } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useLanguage } from '../context/LanguageContext';

const SpecialGramsabhaNotice26Dec2025Form = () => {
  const { language } = useLanguage();
  const componentRef = useRef();

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    const element = componentRef.current;
    const opt = {
      margin: 0,
      filename: language === 'marathi' ? 'Special_Gramsabha_Notice_26Dec2025.pdf' : 'Special_Gramsabha_Notice_26Dec2025_EN.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 3, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(element).set(opt).save();
  };

  return (
    <div className="min-h-screen bg-gray-200 py-8 font-sans print:py-0 print:bg-white flex flex-col items-center">
      <style>
        {`
          .page {
            width: 210mm;
            height: 297mm;
            padding: 20mm;
            margin: 0 auto 20px auto;
            box-sizing: border-box;
            position: relative;
            background-color: white;
            overflow: hidden;
            box-shadow: 0 0 15px rgba(0,0,0,0.1);
          }
          
          .dashed-line {
            border-bottom: 1px dashed black;
            display: inline-block;
          }

          @media print {
            @page {
              size: A4 portrait;
              margin: 0mm !important;
            }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              width: 210mm !important;
              height: 297mm !important;
              background-color: white !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .page {
              margin: 0 !important;
              box-shadow: none !important;
              width: 210mm !important;
              height: 297mm !important;
              page-break-after: always !important;
              break-after: page !important;
              break-inside: avoid !important;
            }
            .no-print {
              display: none !important;
            }
            .min-h-screen {
              min-height: auto !important;
              padding: 0 !important;
            }
          }
        `}
      </style>
      
      {/* Controls Container */}
      <div className="w-full max-w-[210mm] mb-6 flex justify-end space-x-4 print:hidden px-4 no-print">
        <button
          onClick={handlePrint}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm transition-colors"
        >
          <Printer className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Print' : 'Print'}
        </button>
        <button
          onClick={handleDownloadPdf}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 shadow-sm transition-colors"
        >
          <Download className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Download PDF' : 'Download PDF'}
        </button>
      </div>

      <div ref={componentRef} className="flex flex-col items-center">
        {/* PAGE 1 */}
        <div className="bg-white text-black shadow-lg print:shadow-none relative box-border flex flex-col page">
           <div className="text-center mb-2">
              <p className="text-[14px] font-bold">{language === 'marathi' ? 'परिशिष्ट –अ (सभेच्या सुचनेचा नमुना )' : 'Appendix-A (Format of Notice of Meeting)'}</p>
              <p className="text-[12px]">{language === 'marathi' ? '(ग्राम विकास विभाग शासन निर्णय क्र. संग्राम-२०१४ प्र.क्र. ४३/ संग्राम कक्ष दि. २५ जून २०१४ अन्वये )' : '(As per Rural Development Department Government Resolution No. Sangram-2014 C.R. 43/ Sangram Cell dated 25 June 2014)'}</p>
           </div>
           
           <div className="text-center mb-4">
              <h1 className="text-2xl font-bold">{language === 'marathi' ? 'ग्रामसभेची सुचना' : 'Notice of Gramsabha'}</h1>
           </div>

           <div className="flex justify-end text-[16px] mb-6">
              <div className="flex flex-col space-y-1 w-[350px]">
                 <p className="flex items-end">
                    <span className="mr-1">{language === 'marathi' ? 'जा.क्र.------- ग्रा.पं.कार्यालय' : 'Outward No.------- G.P. Office'}</span>
                    <span className="dashed-line flex-grow h-4"></span>
                 </p>
                 <p className="flex items-end">
                    <span className="mr-1">{language === 'marathi' ? 'ता.---------------- जि.' : 'Tal.---------------- Dist.'}</span>
                    <span className="dashed-line flex-grow h-4"></span>
                 </p>
                 <p className="flex items-end">
                    <span className="mr-2 text-bold">{language === 'marathi' ? 'दिनांक:' : 'Date:'}</span>
                    <span className="dashed-line w-12 h-4 mr-1"></span>
                    <span>{language === 'marathi' ? '/१२/२०२५' : '/12/2025'}</span>
                 </p>
              </div>
           </div>

           <div className="text-[16px] mb-4 space-y-2">
              <p>{language === 'marathi' ? 'प्रति,' : 'To,'}</p>
              <p className="flex items-end">
                 <span className="mr-2">{language === 'marathi' ? 'श्री./श्रीमती' : 'Mr./Mrs.'}</span>
                 <span className="dashed-line flex-grow h-4"></span>
              </p>
              <p className="flex items-end">
                 <span className="mr-2">{language === 'marathi' ? 'उपसरपंच/सदस्य, सर्व ग्रामस्थ ग्रामपंचायत' : 'Deputy Sarpanch/Member, All Villagers Gram Panchayat'}</span>
                 <span className="dashed-line flex-grow h-4"></span>
              </p>
           </div>

           <div className="mb-4">
              <h2 className="text-[17px] font-bold">
                {language === 'marathi' ? 'विषय : दिनांक २६ /१२/२०२५ रोजी घेण्यात येणा-या विशेष ग्रामसभेस उपस्थित राहणेबाबत.' : 'Subject: Regarding being present for the Special Gramsabha to be held on 26/12/2025.'}
              </h2>
           </div>

           <div className="text-[16px] space-y-3">
              <p>{language === 'marathi' ? 'महोदय,' : 'Respected Sir,'}</p>
              <p className="flex items-end flex-wrap">
                 <span>{language === 'marathi' ? 'दिनांक २६/१२/२०२५' : "Gram Panchayat's Gramsabha has been organized on"} <span className="font-bold">{language === 'marathi' ? 'शुक्रवार' : 'Friday'}</span> {language === 'marathi' ? 'रोजी ग्रामपंचायत' : '26/12/2025'}</span>
                 <span className="dashed-line flex-grow h-4 mx-1"></span>
                 <span>{language === 'marathi' ? 'ची ग्रामसभा आयोजित करण्यात आली आहे.' : ''}</span>
              </p>
              
              <p className="font-bold flex items-end">
                 <span>{language === 'marathi' ? 'सभेचा दिनांक व वेळ: २६/१२/२०२५ सकाळी/दुपारी/ सायं' : 'Date and time of meeting: 26/12/2025 Morning/Afternoon/Evening'}</span>
                 <span className="dashed-line w-20 h-4 mx-1"></span>
                 <span>{language === 'marathi' ? 'वाजता' : ''}</span>
              </p>

              <p className="font-bold flex items-end">
                 <span>{language === 'marathi' ? 'सभेचे ठिकाण:' : 'Venue of meeting:'}</span>
                 <span className="dashed-line flex-grow h-4 ml-1"></span>
              </p>

              <div className="mt-2">
                 <p className="font-bold mb-2">{language === 'marathi' ? 'सभेची विषयपत्रिका:' : 'Agenda of the meeting:'}</p>
                 <ol className="list-decimal ml-8 space-y-1">
                    <li>{language === 'marathi' ? 'मागील सभा वृत्तांत वाचून कायम करणे.' : 'Reading and confirming the minutes of the previous meeting.'}</li>
                    <li className="text-justify">{language === 'marathi' ? 'VB-G-RAM G अधिनियम मधील विविध तरतुदी तसेच विकसित भारत @2047 या दृष्टीकोनाबाबत चर्चा व मार्गदर्शन' : 'Discussion and guidance on various provisions of the VB-G-RAM G Act as well as the vision of Viksit Bharat @2047'}</li>
                    <li><span className="dashed-line w-full h-4"></span></li>
                    <li><span className="dashed-line w-full h-4"></span></li>
                 </ol>
              </div>

              <div className="mt-4">
                 <p className="text-[16px]">{language === 'marathi' ? 'आयत्यावेळी अध्यक्ष यांच्या परवानगीने येणारे विषय' : 'Subjects coming at the last moment with the permission of the President'}</p>
                 <div className="border border-black p-4 mt-2">
                    <p className="font-bold underline mb-2">{language === 'marathi' ? 'सभेची रुपरेषा :' : 'Outline of the meeting :'}</p>
                    <ul className="list-disc ml-6 space-y-1 text-[14px]">
                       <li>{language === 'marathi' ? 'सरपंच किंवा इतर कोणत्याही अधिकृत व्यक्तीकडून प्रारंभिक मनोगत.' : 'Introductory remarks by the Sarpanch or any other authorized person.'}</li>
                       <li>{language === 'marathi' ? 'ग्रामपंचायत सरपंच व सदस्यांकडून विशेष आमंत्रित मान्यवरांचा सत्कार.' : 'Felicitation of special invitee dignitaries by the Gram Panchayat Sarpanch and members.'}</li>
                       <li>{language === 'marathi' ? 'VB-G-RAM G अधिनियम मधील विविध तरतुदी तसेच विकसित भारत @2047 या दृष्टीकोनाबाबत चर्चा व मार्गदर्शन.' : 'Discussion and guidance on various provisions of the VB-G-RAM G Act as well as the vision of Viksit Bharat @2047.'}</li>
                       <li>{language === 'marathi' ? 'मंत्रालयामार्फत उपलब्ध करून देण्यात आलेल्या अधिनियमाच्या प्रमुख वैशिष्ट्यांचे वितरण.' : 'Distribution of key features of the Act provided through the Ministry.'}</li>
                       <li>{language === 'marathi' ? 'विशेष आमंत्रित मान्यवरांचे मुख्य भाषण.' : 'Keynote address by special invitee dignitaries.'}</li>
                       <li>{language === 'marathi' ? 'बैठकीचा सारांश व राष्ट्रगीत.' : 'Summary of the meeting and National Anthem.'}</li>
                    </ul>
                 </div>
              </div>

              <p className="flex justify-between items-end mt-4">
                 <span>{language === 'marathi' ? 'सदर सभेस आपण उपस्थित रहावे, ही विनंती.' : 'You are requested to attend the said meeting.'}</span>
                 <span>{language === 'marathi' ? 'आपला/आपली,' : 'Yours sincerely,'}</span>
              </p>
           </div>

           <div className="flex justify-end mt-8">
              <div className="flex flex-col items-center w-[350px]">
                 <div className="flex justify-between w-full px-2 mb-1">
                    <p className="font-bold">{language === 'marathi' ? 'ग्रामपंचायत अधिकारी' : 'Gram Panchayat Officer'}</p>
                    <p className="font-bold">{language === 'marathi' ? 'सरपंच' : 'Sarpanch'}</p>
                 </div>
                 <div className="flex items-end w-full">
                    <span className="mr-1 text-sm">{language === 'marathi' ? 'ग्रामपंचायत' : 'Gram Panchayat'}</span>
                    <span className="dashed-line flex-grow h-4"></span>
                 </div>
              </div>
           </div>

           <div className="absolute bottom-8 left-14 text-blue-700 text-sm underline print:hidden">
             www.majhigrampanchayat.com
           </div>
        </div>

        {/* PAGE 2 */}
        <div className="bg-white text-black shadow-lg print:shadow-none relative box-border flex flex-col page">
           <p className="mb-4 text-[16px]">{language === 'marathi' ? 'सदरील ग्रामसभा दिनांक: २६/१२ / २०२५ ची सुचना आमच्या समक्ष नोटीस बोर्डावर डकविण्यात आली.' : 'The notice of the said Gramsabha dated 26/12/2025 was pasted on the notice board in our presence.'}</p>
           <table className="w-full border-collapse border border-black text-[16px]">
              <thead>
                 <tr>
                    <th className="border border-black p-2 w-16 text-center">{language === 'marathi' ? 'अ.क्र.' : 'Sr.No.'}</th>
                    <th className="border border-black p-2 text-center">{language === 'marathi' ? 'ग्रामस्थांचे नाव' : 'Name of Villager'}</th>
                    <th className="border border-black p-2 w-48 text-center">{language === 'marathi' ? 'स्वाक्षरी' : 'Signature'}</th>
                 </tr>
              </thead>
              <tbody>
                 {[...Array(11)].map((_, i) => (
                    <tr key={i} style={{ height: '45px' }}>
                       <td className="border border-black p-2 text-center">{i + 1}</td>
                       <td className="border border-black p-2"></td>
                       <td className="border border-black p-2"></td>
                    </tr>
                 ))}
              </tbody>
           </table>
        </div>
      </div>
    </div>
  );
};

export default SpecialGramsabhaNotice26Dec2025Form;
